import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import SweetAlert2 from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import CONFIG from '../../config';
import './StageEvaluatorDashboard.css';
import Profile from '../AdminDashboard/Profile';
import { SimulationProvider } from '../../contexts/SimulationContext';
import LoadingSpinner from '../common/LoadingSpinner';

const MySwal = withReactContent(SweetAlert2);

const StageEvaluatorDashboard = ({ onLogout }) => {
    const [assignedProjects, setAssignedProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedStage, setSelectedStage] = useState(null);
    const [activeTab, setActiveTab] = useState('evaluator'); // evaluator, processed-history, profile
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Evaluator & Queue State
    const [queueCandidates, setQueueCandidates] = useState([]);
    const [selectedQueueCand, setSelectedQueueCand] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // History Filters
    const [histFilterChest, setHistFilterChest] = useState('');
    const [histFilterUnique, setHistFilterUnique] = useState('');
    const [histFilterBatch, setHistFilterBatch] = useState('');

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        fetchAssignedProjects();
    }, []);

    useEffect(() => {
        if (selectedProject && selectedStage) {
            fetchQueue();
            fetchHistory();
        }
    }, [selectedProject, selectedStage]);

    const fetchQueue = async () => {
        if (!selectedProject || !selectedStage) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/stage-candidates/${selectedProject._id}/${selectedStage.name}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setQueueCandidates(resp.data);
            // If the currently selected candidate is no longer in the queue, clear selection
            if (selectedQueueCand && !resp.data.find(c => c._id === selectedQueueCand._id)) {
                setSelectedQueueCand(null);
            }
        } catch (err) {
            console.error('Failed to fetch queue:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssignedProjects = async () => {
        setInitialLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/projects/assigned`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAssignedProjects(resp.data);
            if (resp.data.length > 0) {
                setSelectedProject(resp.data[0]);
                // Default to first physical stage
                if (resp.data[0].stages && resp.data[0].stages.length > 0) {
                    setSelectedStage(resp.data[0].stages[0]);
                }
            }
        } catch (err) {
            console.error('Failed to fetch assigned projects:', err);
        } finally {
            setInitialLoading(false);
        }
    };

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/stage-history/${selectedProject._id}/${selectedStage.name}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setHistory(resp.data);
        } catch (err) {
            console.error('Failed to fetch history:', err);
        } finally {
            setLoading(false);
        }
    };


    const validate = (val) => {
        if (!selectedStage || !val) return { status: 'PENDING', rule: 'Enter value' };
        const num = parseFloat(val);
        if (isNaN(num)) return { status: 'FAIL', rule: 'Invalid number' };

        let isPass = true;
        let ruleText = '';

        if (selectedStage.validation_rules && selectedStage.validation_rules.length > 0) {
            selectedStage.validation_rules.forEach(rule => {
                if (rule.condition === 'min') {
                    ruleText = `Min: ${rule.value}`;
                    if (num < rule.value) isPass = false;
                }
                if (rule.condition === 'max') {
                    ruleText = `Max: ${rule.value}`;
                    if (num > rule.value) isPass = false;
                }
            });
        }
        return { status: isPass ? 'PASS' : 'FAIL', rule: ruleText };
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!selectedQueueCand || !selectedProject || !selectedStage) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resultValue = Object.values(formData)[0] || '0';
            const { status } = validate(resultValue);

            const stageIndex = selectedProject.stages.findIndex(s => s.name === selectedStage.name);
            const isFinalSubStage = stageIndex === selectedProject.stages.length - 1;
            const nextSubStage = isFinalSubStage ? null : selectedProject.stages[stageIndex + 1].name;

            await axios.post(`${CONFIG.API_URL}/api/candidates/submit-stage-result`, {
                candidateId: selectedQueueCand._id,
                projectId: selectedProject._id,
                stageName: selectedStage.name,
                fields: formData,
                result: status,
                nextSubStage,
                isFinalSubStage
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            MySwal.fire({
                icon: status === 'PASS' ? 'success' : 'warning',
                title: status === 'PASS' ? 'PASS Verified' : 'FAIL Recorded',
                text: `${selectedQueueCand.full_name} processed.`,
                toast: true,
                position: 'top-end',
                timer: 2000,
                showConfirmButton: false
            });

            setSelectedQueueCand(null);
            setFormData({});
            fetchQueue();
            fetchHistory();
        } catch (err) {
            MySwal.fire('Error', 'Failed to save result', 'error');
        } finally {
            setLoading(false);
        }
    };

    const renderEvaluator = () => {
        const filteredQueue = queueCandidates.filter(c =>
            c.chest_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.unique_id.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return (
            <div className="se-eval-grid">
                {/* Left: Waiting Queue */}
                <div className="se-glass-card se-queue-pane">
                    <div className="se-pane-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon icon="ion:people" width="20" color="var(--se-primary)" />
                            <h3 className="se-pane-title">WAITING QUEUE</h3>
                        </div>
                        <span className="se-queue-count">{filteredQueue.length} Candidates</span>
                    </div>

                    <div className="se-search-box">
                        <Icon icon="ion:search-outline" className="se-search-icon" />
                        <input
                            type="text"
                            placeholder="Find candidate (Chest, Name, ID)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    <div className="se-queue-list hide-scrollbar">
                        {filteredQueue.length === 0 ? (
                            <div className="se-empty-state">
                                <div className="se-empty-icon-wrapper">
                                    <Icon icon="ion:people-outline" width="36" />
                                </div>
                                <h4>Queue is Clear</h4>
                                <p>No candidates waiting at this station.<br />Select a different stage or use the simulator.</p>
                            </div>
                        ) : (
                            filteredQueue.map(cand => (
                                <div
                                    key={cand._id}
                                    className={`se-queue-item ${selectedQueueCand?._id === cand._id ? 'active' : ''}`}
                                    onClick={() => setSelectedQueueCand(cand)}
                                >
                                    <div className="se-item-chest">{cand.chest_number}</div>
                                    <div className="se-item-info">
                                        <div className="se-item-name">{cand.full_name}</div>
                                        <div className="se-item-id">ID: {cand.unique_id}</div>
                                    </div>
                                    <Icon icon="ion:chevron-forward" className="se-item-arrow" />
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Right: Active Evaluation */}
                <div className="se-glass-card se-eval-pane">
                    {!selectedQueueCand ? (
                        <div className="se-eval-empty">
                            <div className="se-eval-empty-header">
                                <Icon icon="solar:user-rounded-bold-duotone" width="32" color="white" />
                                <span>STATION READY</span>
                            </div>
                            <div style={{ padding: '60px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                <div className="se-empty-icon-pulse">
                                    <Icon icon="solar:users-group-rounded-bold-duotone" width="64" style={{ color: 'var(--se-primary)', opacity: 0.15 }} />
                                </div>
                                <h3 style={{ fontSize: '24px', fontWeight: 900, color: 'var(--se-text-main)', marginTop: '24px', marginBottom: '12px' }}>AWAITING CANDIDATE</h3>
                                <p style={{ fontSize: '14px', color: 'var(--se-text-muted)', maxWidth: '300px', lineHeight: 1.6, fontWeight: 500 }}>
                                    Please select a candidate from the left queue to begin their evaluation for the <b>{selectedStage?.name || 'current'}</b> stage.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="se-eval-content">
                            <div className="se-eval-header">
                                <div className="se-candidate-header-info">
                                    <div className="se-candidate-avatar-large">
                                        <Icon icon="ion:person" width="32" />
                                    </div>
                                    <div>
                                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 800 }}>{selectedQueueCand.full_name}</h2>
                                        <div style={{ display: 'flex', gap: '15px', marginTop: '4px' }}>
                                            <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--se-primary)' }}>
                                                CHEST: {selectedQueueCand.chest_number}
                                            </span>
                                            <span style={{ fontSize: '13px', color: 'var(--se-text-muted)' }}>
                                                BATCH: {selectedQueueCand.batch_id || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button className="se-btn-cancel" onClick={() => setSelectedQueueCand(null)}>
                                    <Icon icon="ion:close" width="18" /> DESELECT
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="se-eval-form">
                                <div className="se-form-section">
                                    <div className="se-section-header">
                                        <Icon icon="ion:options-outline" color="var(--se-primary)" />
                                        <span>PARAMETER EVALUATION</span>
                                    </div>

                                    <div className="se-fields-grid">
                                        {selectedStage?.measurement_fields.map(field => {
                                            const validation = validate(formData[field] || '');
                                            return (
                                                <div key={field} className="se-form-group">
                                                    <label>{field.replace('_', ' ').toUpperCase()}</label>
                                                    <div className="se-input-wrapper">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            placeholder={`Enter ${field}`}
                                                            value={formData[field] || ''}
                                                            onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                                                            required
                                                        />
                                                        {formData[field] && (
                                                            <div className={`se-status-indicator ${validation.status.toLowerCase()}`}>
                                                                {validation.status}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {formData[field] && (
                                                        <div className="se-validation-hint">
                                                            Rule: {validation.rule}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="se-btn-group">
                                    <button
                                        type="submit"
                                        className={`se-btn-submit ${Object.keys(formData).length > 0 && validate(Object.values(formData)[0]).status === 'PASS' ? 'pass' : 'fail'}`}
                                        disabled={loading || Object.keys(formData).length === 0}
                                        style={{ position: 'relative' }}
                                    >
                                        {loading ? (
                                            <LoadingSpinner compact message="Saving..." />
                                        ) : (
                                            <>
                                                <Icon icon="ion:checkbox-outline" width="20" />
                                                RECORD & SUBMIT RESULT
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderHistory = () => {
        const filteredHist = history.filter(h => {
            const matchChest = h.chest_number.toLowerCase().includes(histFilterChest.toLowerCase());
            const matchUnique = h.unique_id.toLowerCase().includes(histFilterUnique.toLowerCase());
            const matchBatch = (h.batch_id || '').toLowerCase().includes(histFilterBatch.toLowerCase());
            return matchChest && matchUnique && matchBatch;
        });

        return (
            <div className="se-full-pane se-fade-in">
                <div className="se-glass-card">
                    <div className="se-pane-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Icon icon="ion:time" width="20" color="var(--se-primary)" />
                            <h3 className="se-pane-title">RECENT PROCESSED CANDIDATES</h3>
                        </div>
                        <span className="se-queue-count">{filteredHist.length} Total Records</span>
                    </div>

                    {loading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                            <LoadingSpinner message="Fetching History..." />
                        </div>
                    ) : (
                        <>
                            <div className="se-filter-bar">
                        <div className="se-filter-group">
                            <label>CHEST NO</label>
                            <input type="text" value={histFilterChest} onChange={(e) => setHistFilterChest(e.target.value)} placeholder="Filter chest..." />
                        </div>
                        <div className="se-filter-group">
                            <label>UNIQUE ID</label>
                            <input type="text" value={histFilterUnique} onChange={(e) => setHistFilterUnique(e.target.value)} placeholder="Filter ID..." />
                        </div>
                        <div className="se-filter-group">
                            <label>BATCH ID</label>
                            <input type="text" value={histFilterBatch} onChange={(e) => setHistFilterBatch(e.target.value)} placeholder="Filter batch..." />
                        </div>
                    </div>

                    <div className="se-table-container hide-scrollbar" style={{ marginTop: '20px' }}>
                        <table className="se-table">
                            <thead>
                                <tr>
                                    <th>NAME</th>
                                    <th>CHEST NO</th>
                                    <th>UNIQUE ID</th>
                                    <th>BATCH</th>
                                    <th>RECORDED VALUE</th>
                                    <th>RESULT</th>
                                    <th>TIMESTAMP</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredHist.length === 0 ? (
                                    <tr>
                                        <td colSpan="7">
                                            <div className="se-empty-state" style={{ padding: '60px 20px' }}>
                                                <div className="se-empty-icon-wrapper">
                                                    <Icon icon="ion:document-text-outline" width="36" />
                                                </div>
                                                <h4>No Records Found</h4>
                                                <p>No candidates have been processed at this station yet,<br />or no results match your current filters.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredHist.map((h, i) => (
                                        <tr key={i}>
                                            <td style={{ fontWeight: 800 }}>{h.full_name}</td>
                                            <td style={{ color: 'var(--se-primary)', fontWeight: 700 }}>{h.chest_number}</td>
                                            <td style={{ opacity: 0.7, fontSize: '11px' }}>{h.unique_id}</td>
                                            <td style={{ fontWeight: 600 }}>{h.batch_id || 'N/A'}</td>
                                            <td style={{ fontWeight: 800 }}>{h.value}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span className={`se-badge se-badge-${h.result.toLowerCase()}`}>
                                                        {h.result}
                                                    </span>
                                                    <Icon icon={h.result === 'PASS' ? "ion:checkmark-circle" : "ion:close-circle"} color={h.result === 'PASS' ? '#10b981' : '#ef4444'} />
                                                </div>
                                            </td>
                                            <td style={{ fontSize: '11px', opacity: 0.6, fontWeight: 600 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <Icon icon="ion:calendar-outline" />
                                                    {new Date(h.timestamp).toLocaleString()}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
                )}
                </div>
            </div>
        );
    };

    return (
        <SimulationProvider>
            <div className="se-dashboard-container">
                {/* Sidebar */}
                <aside className={`se-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="se-sidebar-toggle" onClick={() => setIsCollapsed(!isCollapsed)}>
                        <Icon icon={isCollapsed ? "ion:chevron-forward" : "ion:chevron-back"} />
                    </div>

                    <div className="se-sidebar-header">
                        <div style={{ width: '42px', height: '42px', background: 'var(--se-primary)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', minWidth: '42px', boxShadow: '0 4px 12px rgba(128,0,0,0.2)' }}>
                            <Icon icon="ion:shield-checkmark" width="22" />
                        </div>
                        {!isCollapsed && <span style={{ fontWeight: 800, fontSize: '18px', color: 'var(--se-primary)', letterSpacing: '-0.5px', whiteSpace: 'nowrap' }}>ICFF INDIA</span>}
                    </div>

                    <nav className="se-nav-container">
                        {[
                            {
                                title: 'STATION CONTROL',
                                items: [
                                    { id: 'evaluator', icon: 'ion:clipboard-outline', label: 'Station Operations' }
                                ]
                            },
                            {
                                title: 'RECORDS',
                                items: [
                                    { id: 'processed-history', icon: 'ion:time-outline', label: 'Recent Processed' }
                                ]
                            },
                            {
                                title: 'USER SETTINGS',
                                items: [
                                    { id: 'profile', icon: 'ion:person-outline', label: 'My Profile' }
                                ]
                            }
                        ].map((group, groupIdx) => (
                            <React.Fragment key={groupIdx}>
                                {!isCollapsed && (
                                    <div className="se-sidebar-heading">
                                        {group.title}
                                    </div>
                                )}
                                {group.items.map(item => (
                                    <button
                                        key={item.id}
                                        className={`se-nav-item ${activeTab === item.id ? 'active' : ''}`}
                                        onClick={() => setActiveTab(item.id)}
                                    >
                                        {activeTab === item.id && <motion.div layoutId="seActiveTabPill" className="se-nav-active-pill" />}
                                        {activeTab === item.id && <motion.div layoutId="seActiveTabIndicator" className="se-nav-active-indicator" />}
                                        <div className="se-nav-inner">
                                            <Icon icon={item.icon} width="22" />
                                            {!isCollapsed && <span className="se-nav-label">{item.label}</span>}
                                        </div>
                                    </button>
                                ))}
                            </React.Fragment>
                        ))}
                    </nav>

                    <div className="se-sidebar-footer" style={{ padding: '20px' }}>
                        <button className="universal-logout-btn" onClick={onLogout}>
                            <Icon icon="ion:log-out-outline" width="22" />
                            {!isCollapsed && <span className="se-nav-label" style={{ fontWeight: 700 }}>Logout</span>}
                        </button>
                    </div>
                </aside>

                {/* Main Area */}
                <div className="se-content-area">
                    <header className="se-navbar">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--se-text-muted)' }}>
                                STATION: <span style={{ color: 'var(--se-primary)' }}>{selectedStage?.name || 'NOT ASSIGNED'}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <select
                                value={selectedProject?._id || ''}
                                onChange={(e) => {
                                    const p = assignedProjects.find(proj => proj._id === e.target.value);
                                    setSelectedProject(p);
                                    if (p && p.stages.length > 0) setSelectedStage(p.stages[0]);
                                }}
                                style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid var(--se-border-light)', fontSize: '13px', fontWeight: 700, minWidth: '200px' }}
                            >
                                {assignedProjects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                            </select>

                            <select
                                value={selectedStage?.name || ''}
                                onChange={(e) => setSelectedStage(selectedProject.stages.find(s => s.name === e.target.value))}
                                style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid var(--se-border-light)', fontSize: '13px', fontWeight: 700, minWidth: '150px' }}
                            >
                                {selectedProject?.stages.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                            </select>
                        </div>

                        <div className="se-profile-pill" onClick={() => setActiveTab('profile')}>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '13px', fontWeight: 700 }}>{user.name}</div>
                                <div style={{ fontSize: '10px', color: 'var(--se-primary)', fontWeight: 800 }}>STAGE EVALUATOR</div>
                            </div>
                            <div className="se-profile-avatar">
                                <Icon icon="ion:person" width="20" />
                            </div>
                        </div>
                    </header>

                    <main className="se-main-view" style={{ flex: 1 }}>
                        {initialLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <LoadingSpinner message="Initializing Station Operations..." />
                            </div>
                        ) : activeTab === 'evaluator' ? renderEvaluator() :
                            activeTab === 'processed-history' ? renderHistory() :
                                <Profile user={user} />}
                    </main>
                </div>


            </div>
        </SimulationProvider>
    );
};

export default StageEvaluatorDashboard;
